// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import ReportPageScaffold from '../components/ReportPageScaffold';
import DateRangeFilter from '../components/DateRangeFilter';
import PaginationFooter from '../components/PaginationFooter';
import { ReportLoadingState } from '../components/ReportLoadingState';
import { useState, useEffect, useMemo } from 'react';
import { Download, SlidersHorizontal } from 'lucide-react';
import { attendanceAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import ImageLightbox from '../components/ImageLightbox';
import CustomReportPanel from '../components/CustomReportPanel';
import { getLocalYesterdayDateString } from '../utils/dateHelpers';
import { isAdminForCurrentProject } from '../utils/isAdminForCurrentProject';
import { PAGE_SIZE_OPTIONS } from '../constants/reportPageSize';
import { buildCsv, downloadCsv } from '../utils/csv';
import { getImageViewUrl, getFullImageViewUrl } from '../utils/imageUrl';
import { usePagination } from '../hooks/usePagination';
import { useReportFilters } from '../hooks/useReportFilters';
import { useTableSort, SortTh, type SortAccessors } from '../components/dashboard/useTableSort';
import DashboardFilterModal from '../components/dashboard/DashboardFilterModal';
import { useStoreAttrFilters } from '../hooks/useStoreAttrFilters';

// Default both From and To to yesterday — most users open this report the morning
// after to review the previous day's marked / missed attendance windows.


interface RandomAttendanceLog {
  id: number;
  emp_code: string;
  employeeCode: string;
  employeeName: string;
  store_name: string | null;
  storeName: string | null;
  store_code: string | null;
  storeCode: string | null;
  trade: string | null;
  storeTrade: string | null;
  category_name: string | null;
  storeCategory: string | null;
  city: string | null;
  state: string | null;
  notification_time: string;
  notification_slot?: number;
  date: string;
  time: string;
  status: string;
  selfie_url: string | null;
  // Whether this slot demanded a selfie or only an "I'm present at the store"
  // confirmation — decided randomly per slot when the slot is created. NULL on
  // every row predating the feature, and read as "selfie was required".
  selfie_required: boolean | null;
  location_address: string | null;
  locationLink: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface SlotCell {
  id: number;
  time: string;
  status: string;
  latitude: number | null;
  longitude: number | null;
  locationLink: string | null;
  selfie_url: string | null;
  selfie_required: boolean | null;
}

interface PivotedLog {
  key: string;
  employeeCode: string;
  employeeName: string;
  storeName: string | null;
  storeCode: string | null;
  storeTrade: string | null;
  storeCategory: string | null;
  city: string | null;
  state: string | null;
  date: string;
  latestTime: string;
  slot1: SlotCell | null;
  slot2: SlotCell | null;
}

// ── Column sorting ───────────────────────────────────────────────────────────
// Slot TIME renders the API's 24-hour 'HH:MM:SS' as-is; sort on seconds past
// midnight (a number sorts far faster than text on month-long ranges). The '—'
// placeholder (no notification time) matches nothing → a blank.
const slotTimeSortValue = (cell: SlotCell | null): number | null => {
  const match = cell ? /^(\d{1,2}):(\d{2}):(\d{2})/.exec(cell.time) : null;
  return match ? Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3]) : null;
};

// Slot VERIFICATION sorts on what the cell shows, in the cell's own precedence:
// Missed / Day Ended pills, "Selfie" for the photo thumbnail, then the
// Confirmed — no selfie pill; '—' is a blank. Deliberately data-only: a thumbnail
// that fails to load still sorts as "Selfie", so rows don't jump as images error out.
const slotVerificationSortValue = (cell: SlotCell | null): string | null => {
  if (!cell) return null;
  if (cell.status === 'missed') return 'Missed';
  if (cell.status === 'cancelled') return 'Day Ended';
  if (getImageViewUrl(cell.selfie_url)) return 'Selfie';
  if (cell.selfie_required === false) return 'Confirmed — no selfie';
  return null;
};

// One accessor per sortable column, defined once at module level so the sort isn't
// re-run on every unrelated re-render (page flips, lightbox, broken-image updates)
// of what can be a many-thousand-row date range.
const SORT_ACCESSORS: SortAccessors<PivotedLog> = {
  employeeCode: (g) => g.employeeCode,
  employeeName: (g) => (g.employeeName === 'N/A' ? null : g.employeeName), // pivot's "unknown" placeholder
  storeName: (g) => g.storeName,
  storeCode: (g) => g.storeCode,
  storeTrade: (g) => g.storeTrade,
  storeCategory: (g) => g.storeCategory,
  city: (g) => g.city,
  state: (g) => g.state,
  date: (g) => g.date, // YYYY-MM-DD → sorts chronologically as text
  slot1Time: (g) => slotTimeSortValue(g.slot1),
  slot1Verification: (g) => slotVerificationSortValue(g.slot1),
  slot2Time: (g) => slotTimeSortValue(g.slot2),
  slot2Verification: (g) => slotVerificationSortValue(g.slot2),
};

export default function RandomAttendanceLogsPage() {
  const { user, selectedProjectId } = useAuthStore();
  const [attendanceLogs, setAttendanceLogs] = useState<RandomAttendanceLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  
  // Filters - Default to yesterday. The pending → applied date model and the
  // pagination state are shared verbatim with the other report pages — see
  // hooks/useReportFilters. Editing the date inputs only updates pending state;
  // the fetch effect watches the applied copy so changing dates does not
  // immediately trigger the heavy backend query — the user must click Apply.
  const {
    startDate, setStartDate,
    endDate, setEndDate,
    appliedStartDate, appliedEndDate,
    searchQuery, setSearchQuery,
    pageSize, setPageSize,
    currentPage, setCurrentPage,
    hasPendingDateChanges,
    applyDateRange,
  } = useReportFilters({
    initialStartDate: getLocalYesterdayDateString(),
    initialEndDate: getLocalYesterdayDateString(),
  });
  const [statusFilter, setStatusFilter] = useState('');

  const fetchRandomAttendanceLogs = async () => {
    setLoading(true);
    try {
      // Send employeeCode only for non-admin callers. Admins (super_admin, or
      // project admin for the current project) need to see the full project
      // roster, so we omit employeeCode to skip the backend's hierarchy filter
      // entirely. This bypasses the fragile session/DB admin detection on the
      // backend that has been observed to silently drop real super admins into
      // hierarchy scoping.
      const isAdmin = isAdminForCurrentProject(user, selectedProjectId);

      const response = await attendanceAPI.getRandomAttendanceLogs({
        start_date: appliedStartDate,
        end_date: appliedEndDate,
        status: statusFilter || undefined,
        search: searchQuery || undefined,
        employeeCode: isAdmin ? undefined : user?.userName,
        projectId: selectedProjectId ?? undefined,
        // Omit `limit` so the backend returns every slot row for the selected
        // date range (no artificial cap) — the report loads the full set and
        // paginates client-side, matching the Daily Attendance report. Large
        // projects (e.g. Apple iPro at ~2,300 slots/day) over a month were being
        // silently truncated to the newest 5000 raw rows before this.
      });
      
      setAttendanceLogs(response.data?.data || []);
    } catch (err: any) {
      console.error('Error fetching random attendance logs:', err);
      setAttendanceLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRandomAttendanceLogs();
  }, [appliedStartDate, appliedEndDate, selectedProjectId]);

  // Store-attribute filters (Trade / Category / Region / City) — shared with the other
  // report pages. Rows carry both the raw SQL spelling and a camelCase alias, so each
  // accessor takes whichever is populated. Region is derived from the state.
  const [storeFiltersOpen, setStoreFiltersOpen] = useState(false);
  const storeFilters = useStoreAttrFilters(attendanceLogs, {
    trade: (r) => r.storeTrade ?? r.trade,
    category: (r) => r.storeCategory ?? r.category_name,
    city: (r) => r.city,
    state: (r) => r.state,
  });

  // Reset to page 1 when filters narrow the result set — otherwise the user
  // searches and lands on an empty page 5.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, storeFilters.activeCount, pageSize]);

  // Extract unique statuses from attendance data for dynamic filter
  const availableStatuses = useMemo(() => {
    const statusSet = new Set<string>();
    attendanceLogs.forEach(log => {
      if (log.status) {
        statusSet.add(log.status);
      }
    });
    return Array.from(statusSet).sort();
  }, [attendanceLogs]);

  const handleExportCSV = () => {
    const headers = [
      'Employee Code', 'Employee Name', 'Store Name', 'Store Code',
      'Store Trade', 'Store Category', 'City', 'State', 'Date',
      'Slot 1 Time', 'Slot 1 Location', 'Slot 1 Verification',
      'Slot 2 Time', 'Slot 2 Location', 'Slot 2 Verification',
    ];


    const slotFields = (cell: SlotCell | null) => {
      if (!cell) return ['-', '-', '-'];
      const label =
        cell.status === 'missed' ? 'Missed' :
        cell.status === 'cancelled' ? 'Day Ended' :
        // No photo was ever requested for this slot — say so, rather than let it read
        // as a missing/failed selfie upload. Guarded on `!cell.selfie_url` for the same
        // reason as the table cell: during the rollout window an old app build ignores
        // the flag and still uploads a photo, and a real photo URL beats this label.
        (cell.selfie_required === false && !cell.selfie_url) ? 'Not required' :
        null;
      return [
        cell.time || '-',
        cell.locationLink || '-',
        label ?? getFullImageViewUrl(cell.selfie_url),
      ];
    };

    const csvData = pivotedLogs.map(group => [
      group.employeeCode,
      group.employeeName,
      group.storeName || '-',
      group.storeCode || '-',
      group.storeTrade || '-',
      group.storeCategory || '-',
      group.city || '-',
      group.state || '-',
      group.date,
      ...slotFields(group.slot1),
      ...slotFields(group.slot2),
    ]);

    downloadCsv(`random-attendance-logs-${startDate}-to-${endDate}.csv`, buildCsv(headers, csvData));
  };

  const filteredLogs = useMemo(() => {
    return attendanceLogs.filter(log => {
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (log.employeeName || '').toLowerCase().includes(q) ||
        (log.employeeCode || log.emp_code || '').toLowerCase().includes(q) ||
        (log.storeName || log.store_name || '').toLowerCase().includes(q) ||
        (log.storeCode || log.store_code || '').toLowerCase().includes(q);
      const matchesStatus = !statusFilter || log.status === statusFilter;
      return matchesSearch && matchesStatus && storeFilters.matches(log);
    });
    // `matches` is stable per filter selection (the object around it is not).
  }, [attendanceLogs, searchQuery, statusFilter, storeFilters.matches]);

  // Pivot: group slot rows by (employee, date) so Slot 1 and Slot 2 render side-by-side.
  const pivotedLogs = useMemo<PivotedLog[]>(() => {
    const groups = new Map<string, PivotedLog>();
    filteredLogs.forEach(log => {
      const empCode = log.employeeCode || log.emp_code;
      const date = log.date || (log.notification_time ? new Date(log.notification_time).toISOString().split('T')[0] : '');
      if (!empCode || !date) return;
      const key = `${empCode}__${date}`;
      let group = groups.get(key);
      if (!group) {
        group = {
          key,
          employeeCode: empCode,
          employeeName: log.employeeName || 'N/A',
          storeName: log.storeName || log.store_name || null,
          storeCode: log.storeCode || log.store_code || null,
          storeTrade: log.storeTrade || log.trade || null,
          storeCategory: log.storeCategory || log.category_name || null,
          city: log.city || null,
          state: log.state || null,
          date,
          latestTime: log.notification_time,
          slot1: null,
          slot2: null,
        };
        groups.set(key, group);
      }
      const cell: SlotCell = {
        id: log.id,
        time: log.time || (log.notification_time ? new Date(log.notification_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—'),
        status: log.status,
        latitude: log.latitude,
        longitude: log.longitude,
        locationLink: log.locationLink || (log.latitude && log.longitude ? `https://www.google.com/maps?q=${log.latitude},${log.longitude}` : null),
        selfie_url: log.selfie_url,
        selfie_required: log.selfie_required ?? null,
      };
      const slot = log.notification_slot;
      if (slot === 1) group.slot1 = cell;
      else if (slot === 2) group.slot2 = cell;
      if (log.notification_time && (!group.latestTime || new Date(log.notification_time).getTime() > new Date(group.latestTime).getTime())) {
        group.latestTime = log.notification_time;
      }
    });
    return Array.from(groups.values()).sort(
      (a, b) => new Date(b.latestTime || 0).getTime() - new Date(a.latestTime || 0).getTime()
    );
  }, [filteredLogs]);

  // Column sorting runs on the WHOLE pivoted set, then pagination slices the result.
  // No default column: the un-sorted order (latest notification first) isn't any one
  // column, and clearing a sort returns to it. A new sort order starts from page 1.
  const sort = useTableSort(pivotedLogs, SORT_ACCESSORS);
  useEffect(() => {
    setCurrentPage(1);
  }, [sort.sortKey, sort.sortDir]);

  const { totalPages, safePage, paginatedData: paginatedLogs } = usePagination(sort.sorted, currentPage, pageSize);

  return (
    <CustomReportPanel
      sourceKey="random-attendance"
      sourceLabel="Random Attendance"
      promptPlaceholder='e.g. "Stores with the highest miss rate in the last 7 days, as a pie chart"'
    >
    <ReportPageScaffold
      title="Random Attendance"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder="Search by Employee Name / Code or Store Name / Code..."
      filters={
        <>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            hasPendingDateChanges={hasPendingDateChanges}
            onStartDateChange={(e) => setStartDate(e.target.value)}
            onEndDateChange={(e) => setEndDate(e.target.value)}
            onStartDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onEndDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onApply={applyDateRange}
          />

          <button
            type="button"
            onClick={() => setStoreFiltersOpen(true)}
            className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium shadow-sm transition ${
              storeFilters.activeCount > 0
                ? 'border-primary-200 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
            title="Filter by store trade, category, region or city"
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
            {storeFilters.activeCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-bold text-white">{storeFilters.activeCount}</span>
            )}
          </button>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Status</option>
            {availableStatuses.map(status => (
              <option key={status} value={status}>
                {status === 'cancelled' ? 'Day Ended' : status.charAt(0).toUpperCase() + status.slice(1)}
              </option>
            ))}
          </select>

          <button
            onClick={handleExportCSV}
            disabled={pivotedLogs.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </>
      }
    >
        {/* Loading State */}
        {loading && <ReportLoadingState />}

        {/* No Records Found */}
        {!loading && pivotedLogs.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-gray-500">No records found</p>
          </div>
        )}

        {/* Table */}
        {!loading && pivotedLogs.length > 0 && (
        <div className="overflow-hidden mt-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/70">
              <tr>
                <SortTh rowSpan={2} align="center" label="EMPLOYEE CODE" sortKey="employeeCode" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <SortTh rowSpan={2} align="center" label="EMPLOYEE NAME" sortKey="employeeName" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <SortTh rowSpan={2} align="center" label="STORE NAME" sortKey="storeName" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <SortTh rowSpan={2} align="center" label="STORE CODE" sortKey="storeCode" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <SortTh rowSpan={2} align="center" label="STORE TRADE" sortKey="storeTrade" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <SortTh rowSpan={2} align="center" label="STORE CATEGORY" sortKey="storeCategory" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <SortTh rowSpan={2} align="center" label="CITY" sortKey="city" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <SortTh rowSpan={2} align="center" label="STATE" sortKey="state" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <SortTh rowSpan={2} align="center" label="DATE" sortKey="date" sort={sort} className="px-4 py-3 text-center text-xs font-medium text-gray-500 whitespace-nowrap align-middle" />
                <th colSpan={3} className="px-4 py-3 text-center text-xs font-semibold text-gray-600 whitespace-nowrap border-l border-gray-200 bg-gray-100/80">
                  SLOT 1
                </th>
                <th colSpan={3} className="px-4 py-3 text-center text-xs font-semibold text-gray-600 whitespace-nowrap border-l border-gray-200 bg-gray-100/80">
                  SLOT 2
                </th>
              </tr>
              <tr>
                <SortTh label="TIME" sortKey="slot1Time" sort={sort} align="center" className="px-4 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap border-l border-gray-200" />
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">LOCATION</th>
                <SortTh label="VERIFICATION" sortKey="slot1Verification" sort={sort} align="center" className="px-4 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap" /> {/* gitleaks:allow — React table sort-key, not a secret */}
                <SortTh label="TIME" sortKey="slot2Time" sort={sort} align="center" className="px-4 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap border-l border-gray-200" />
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap">LOCATION</th>
                <SortTh label="VERIFICATION" sortKey="slot2Verification" sort={sort} align="center" className="px-4 py-2 text-center text-xs font-medium text-gray-500 whitespace-nowrap" /> {/* gitleaks:allow — React table sort-key, not a secret */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedLogs.map((group) => {
                const renderSlotCells = (cell: SlotCell | null) => {
                  const leftBorder = 'border-l border-gray-200';
                  if (!cell) {
                    return (
                      <>
                        <td className={`px-4 py-4 whitespace-nowrap text-sm text-gray-400 text-center ${leftBorder}`}>—</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-400 text-center">—</td>
                        <td className="px-4 py-4 text-sm text-gray-400 text-center">—</td>
                      </>
                    );
                  }
                  const isMissed = cell.status === 'missed';
                  const isCancelled = cell.status === 'cancelled';
                  const selfieUrl = getImageViewUrl(cell.selfie_url);
                  return (
                    <>
                      <td className={`px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center ${leftBorder}`}>
                        {cell.time}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                        {cell.locationLink ? (
                          <a
                            href={cell.locationLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            View Map
                          </a>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-center">
                        {isMissed ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            Missed
                          </span>
                        ) : isCancelled ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                            Day Ended
                          </span>
                        ) : selfieUrl && !brokenImages.has(cell.id) ? (
                          <div className="flex justify-center">
                            <button
                              onClick={() => setLightboxUrl(selfieUrl)}
                              className="block"
                            >
                              <img
                                src={selfieUrl}
                                alt="Selfie"
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                                style={{ imageOrientation: 'from-image' }}
                                onError={() => {
                                  setBrokenImages(prev => new Set(prev).add(cell.id));
                                }}
                              />
                            </button>
                          </div>
                        ) : cell.selfie_required === false ? (
                          // The system deliberately did NOT ask for a photo on this slot —
                          // the employee confirmed presence and the geofence check still
                          // passed. Distinct from the em-dash below, which means a selfie
                          // WAS expected and is missing (failed upload).
                          //
                          // Checked AFTER the thumbnail on purpose: during the rollout
                          // window (backend live, new app build not yet) an old build
                          // ignores the flag and still uploads a selfie, producing rows
                          // that are selfie_required=false WITH a photo. Testing this
                          // first would hide that real photo behind the pill.
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Confirmed — no selfie
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </>
                  );
                };

                return (
                  <tr key={group.key} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {group.employeeCode}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                      {group.employeeName}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {group.storeName || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {group.storeCode || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {group.storeTrade || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {group.storeCategory || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {group.city || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {group.state || '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {group.date}
                    </td>
                    {renderSlotCells(group.slot1)}
                    {renderSlotCells(group.slot2)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
        )}

        {/* 3-column footer: rows-per-page left, page nav truly centered,
            empty spacer right so nothing falls under the fixed SSI /
            "New custom report" widget at bottom-right. */}
        {!loading && pivotedLogs.length > 0 && (
          <PaginationFooter
            safePage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalRows={pivotedLogs.length}
            onPageSizeChange={(value) => setPageSize(value)}
            onPrevPage={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        )}
      <DashboardFilterModal
        open={storeFiltersOpen}
        onClose={() => setStoreFiltersOpen(false)}
        title="Filters"
        sections={storeFilters.sections}
        activeCount={storeFilters.activeCount}
        onClearAll={storeFilters.clear}
      />

      <ImageLightbox
        src={lightboxUrl || ''}
        alt="Attendance Selfie"
        isOpen={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
      />
    </ReportPageScaffold>
    </CustomReportPanel>
  );
}
